import React, { useState, useEffect } from 'react';
import { getAdminFaqs, deleteFaq, reorderFaqs } from '../../services/api';
import { Faq } from '../../types';
import { useTranslation } from '../../context/TranslationContext';
import { useAuth } from '../../context/AuthContext';
import FaqModal from './components/FaqModal';
import './FaqListPage.css';

const FaqListPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentFaq, setCurrentFaq] = useState<Faq | null>(null);
  const [isReordering, setIsReordering] = useState<boolean>(false);
  const [reorderedFaqs, setReorderedFaqs] = useState<Faq[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchFaqs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchFaqs = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const data = await getAdminFaqs(token);
      setFaqs(data);
      setReorderedFaqs(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError(t('admin.common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddFaq = () => {
    setCurrentFaq(null);
    setShowModal(true);
  };

  const handleEditFaq = (faq: Faq) => {
    setCurrentFaq(faq);
    setShowModal(true);
  };

  const handleDeleteFaq = async (id: string | number) => {
    if (!token) return;
    
    if (!window.confirm(t('admin.faqs.confirmDelete'))) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteFaq(id.toString(), token);
      setSuccessMessage(t('admin.faqs.faqDeleted'));
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchFaqs();
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      setError(t('admin.common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = (refreshNeeded: boolean) => {
    setShowModal(false);
    if (refreshNeeded) {
      fetchFaqs();
    }
  };

  const toggleReordering = () => {
    setIsReordering(!isReordering);
    setReorderedFaqs([...faqs]);
  };

  const handleReorder = (dragIndex: number, hoverIndex: number) => {
    const draggedFaq = reorderedFaqs[dragIndex];
    const newFaqs = [...reorderedFaqs];
    newFaqs.splice(dragIndex, 1);
    newFaqs.splice(hoverIndex, 0, draggedFaq);
    
    // Update order values
    const updatedFaqs = newFaqs.map((faq, index) => ({
      ...faq,
      order: index + 1
    }));
    
    setReorderedFaqs(updatedFaqs);
  };

  const saveReordering = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      
      // Filter out FAQs without valid IDs and ensure IDs are numbers when possible
      const orderData = reorderedFaqs
        .filter(faq => faq.id || faq._id)
        .map((faq, index) => {
          // Try to use numeric ID first, then string ID, but never empty string
          const id = faq.id || (faq._id ? faq._id : null);
          if (id === null) return null;
          
          return {
            id: id,
            order: index + 1
          };
        })
        .filter((item): item is { id: string | number; order: number } => item !== null);
      
      if (orderData.length === 0) {
        setError('No valid FAQ IDs found for reordering');
        setLoading(false);
        return;
      }
      
      // Cast to the expected type to satisfy TypeScript
      const typedOrderData: { id: string | number; order: number }[] = orderData;
      await reorderFaqs(typedOrderData, token);
      setSuccessMessage(t('admin.faqs.faqUpdated'));
      setTimeout(() => setSuccessMessage(null), 3000);
      setIsReordering(false);
      fetchFaqs();
    } catch (err) {
      console.error('Error reordering FAQs:', err);
      setError(t('admin.common.error'));
    } finally {
      setLoading(false);
    }
  };

  const FaqItem: React.FC<{ faq: Faq, index: number }> = ({ faq, index }) => {
    const id = faq.id?.toString() || faq._id || '';
    
    return (
      <div className="faq-item" key={id}>
        <div className="faq-content">
          <div className="faq-order">{index + 1}</div>
          <div className="faq-details">
            <h3>{faq.question}</h3>
            <p>{faq.answer}</p>
          </div>
          <div className="faq-status">
            {faq.active ? (
              <span className="status active">{t('admin.common.yes')}</span>
            ) : (
              <span className="status inactive">{t('admin.common.no')}</span>
            )}
          </div>
        </div>
        <div className="faq-actions">
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => handleEditFaq(faq)}
          >
            {t('admin.common.edit')}
          </button>
          <button 
            className="btn btn-sm btn-danger"
            onClick={() => handleDeleteFaq(id)}
          >
            {t('admin.common.delete')}
          </button>
        </div>
      </div>
    );
  };

  const ReorderableFaqItem: React.FC<{ faq: Faq, index: number }> = ({ faq, index }) => {
    const id = faq.id?.toString() || faq._id || '';
    
    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
      e.dataTransfer.setData('text/plain', index.toString());
      e.currentTarget.classList.add('dragging');
    };
    
    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
      e.currentTarget.classList.remove('dragging');
    };
    
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.add('drag-over');
    };
    
    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.currentTarget.classList.remove('drag-over');
    };
    
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.remove('drag-over');
      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
      handleReorder(dragIndex, index);
    };
    
    return (
      <div 
        className="faq-item reorderable"
        key={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="faq-content">
          <div className="faq-order">{index + 1}</div>
          <div className="faq-details">
            <h3>{faq.question}</h3>
          </div>
          <div className="drag-handle">
            <span>≡</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-page faq-list-page">
      <div className="page-header">
        <h1>{t('admin.faqs.title')}</h1>
        <div className="header-actions">
          {isReordering ? (
            <>
              <button 
                className="btn btn-secondary"
                onClick={toggleReordering}
              >
                {t('admin.common.cancel')}
              </button>
              <button 
                className="btn btn-primary"
                onClick={saveReordering}
                disabled={loading}
              >
                {t('admin.faqs.saveOrder')}
              </button>
            </>
          ) : (
            <>
              <button 
                className="btn btn-secondary"
                onClick={toggleReordering}
              >
                {t('admin.faqs.reorderFaqs')}
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAddFaq}
              >
                {t('admin.faqs.addFaq')}
              </button>
            </>
          )}
        </div>
      </div>
      
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}
      
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      {loading ? (
        <div className="loading">{t('admin.common.loading')}</div>
      ) : (
        <>
          {isReordering ? (
            <div className="reordering-instructions">
              {t('admin.faqs.reorderInstructions')}
            </div>
          ) : null}
          
          <div className="faq-list">
            {isReordering ? (
              reorderedFaqs.length > 0 ? (
                reorderedFaqs.map((faq, index) => (
                  <ReorderableFaqItem 
                    key={faq.id?.toString() || faq._id}
                    faq={faq}
                    index={index}
                  />
                ))
              ) : (
                <div className="no-data">{t('admin.common.noData')}</div>
              )
            ) : (
              faqs.length > 0 ? (
                faqs.map((faq, index) => (
                  <FaqItem 
                    key={faq.id?.toString() || faq._id}
                    faq={faq}
                    index={index}
                  />
                ))
              ) : (
                <div className="no-data">{t('admin.common.noData')}</div>
              )
            )}
          </div>
        </>
      )}
      
      {showModal && (
        <FaqModal
          faq={currentFaq}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default FaqListPage;
